/**
 * Estados de erro do FaceLivenessDetector (Amplify) → mensagem da marca.
 * O objeto de erro traz `state` (ex.: `CAMERA_ACCESS_ERROR`).
 */
export type LivenessFailure = { title: string; message: string; retryable: boolean };

const BY_STATE: Record<string, LivenessFailure> = {
  CAMERA_ACCESS_ERROR: {
    title: 'Sem acesso à câmera',
    message: 'Libere a permissão de câmera para este site nas configurações do navegador e tente de novo.',
    retryable: true,
  },
  DEFAULT_CAMERA_NOT_FOUND_ERROR: {
    title: 'Câmera não encontrada',
    message: 'Não achamos uma câmera frontal neste aparelho. Use um celular ou notebook com webcam.',
    retryable: true,
  },
  CAMERA_FRAMERATE_ERROR: {
    title: 'Câmera abaixo do mínimo',
    message: 'A câmera não entrega 15 quadros por segundo. Feche outros apps que usam a câmera e tente de novo.',
    retryable: true,
  },
  TIMEOUT: {
    title: 'O tempo acabou',
    message: 'Não conseguimos enquadrar seu rosto a tempo. Centralize no oval e aproxime devagar.',
    retryable: true,
  },
  FRESHNESS_TIMEOUT: {
    title: 'Quase lá',
    message: 'Fique parado enquanto a tela muda de cor — é a parte que confirma que você está ao vivo.',
    retryable: true,
  },
  FACE_DISTANCE_ERROR: {
    title: 'Rosto perto demais no início',
    message: 'Comece um pouco mais afastado da câmera e aproxime quando a tela pedir.',
    retryable: true,
  },
  MULTIPLE_FACES_ERROR: {
    title: 'Mais de uma pessoa',
    message: 'Só o seu rosto pode aparecer na câmera durante a prova de vida.',
    retryable: true,
  },
  MOBILE_LANDSCAPE_ERROR: {
    title: 'Vire o aparelho',
    message: 'A prova de vida só funciona com o celular na vertical.',
    retryable: true,
  },
  CONNECTION_TIMEOUT: {
    title: 'Conexão instável',
    message: 'A ligação com o serviço caiu. Confira a internet e tente de novo.',
    retryable: true,
  },
  SERVER_ERROR: {
    title: 'Serviço indisponível',
    message: 'O serviço de prova de vida não respondeu. Tente de novo em instantes.',
    retryable: true,
  },
  RUNTIME_ERROR: {
    title: 'Algo interrompeu a verificação',
    message: 'Recarregue a página se acontecer de novo.',
    retryable: true,
  },
};

const GENERIC: LivenessFailure = {
  title: 'Não deu certo',
  message: 'A prova de vida foi interrompida. Tente de novo.',
  retryable: true,
};

export function describeLivenessError(error: unknown): LivenessFailure {
  if (error !== null && typeof error === 'object' && 'state' in error) {
    const state = String((error as { state: unknown }).state);
    return BY_STATE[state] ?? GENERIC;
  }
  return GENERIC;
}
